/**
 * バリデーション関数統一
 * 全体で使用するバリデーション処理を集約
 */

import { YokaiInput } from '../types/yokai';
import { logger } from './logger';
import { ErrorHandler } from './errorHandler';

/**
 * 文字列の trim とバリデーション
 */
export const validateAndTrim = (value: string): string => {
  return value.trim();
};

/**
 * 必須フィールドの検証
 */
export const validateRequired = (value: string, fieldName: string): void => {
  const trimmed = validateAndTrim(value);
  if (!trimmed) {
    throw ErrorHandler.handleValidationError(
      `${fieldName}は必須項目です。`,
      fieldName
    );
  }
};

/**
 * 文字数制限の検証
 */
export const validateLength = (
  value: string, 
  maxLength: number, 
  fieldName: string,
  minLength: number = 0
): void => {
  const trimmed = validateAndTrim(value);
  
  if (trimmed.length < minLength) {
    throw ErrorHandler.handleValidationError(
      `${fieldName}は${minLength}文字以上で入力してください。`,
      fieldName
    );
  }
  
  if (trimmed.length > maxLength) {
    throw ErrorHandler.handleValidationError(
      `${fieldName}は${maxLength}文字以内で入力してください。`,
      fieldName
    );
  }
};

/**
 * 妖怪入力データの包括的バリデーション
 */
export const validateYokaiInput = (input: YokaiInput): YokaiInput => {
  logger.debug('VALIDATION', '妖怪入力データ検証開始', input);

  // 生息地は必須
  validateRequired(input.habitat, '妖怪の生息地');
  validateLength(input.habitat, 20, '妖怪の生息地', 1);

  // 見た目は任意だが、入力されている場合は文字数制限あり
  if (input.appearance && validateAndTrim(input.appearance)) {
    validateLength(input.appearance, 100, '妖怪の見た目');
  }

  // 属性は任意だが、入力されている場合は文字数制限あり
  if (input.attribute && validateAndTrim(input.attribute)) {
    validateLength(input.attribute, 30, '妖怪の属性');
  }


  // 正規化されたデータを返す
  const validatedInput: YokaiInput = {
    habitat: validateAndTrim(input.habitat),
    appearance: validateAndTrim(input.appearance),
    attribute: validateAndTrim(input.attribute)
  };

  logger.debug('VALIDATION', '妖怪入力データ検証完了', validatedInput);
  return validatedInput;
};

/**
 * URLの検証
 */
export const validateUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * 画像URLの検証
 */
export const validateImageUrl = (url: string): void => {
  if (!validateUrl(url)) {
    throw ErrorHandler.handleValidationError(
      '無効な画像URLです。',
      'imageUrl'
    );
  }

  // 画像ファイルの拡張子チェック（緩い）
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  const hasImageExtension = imageExtensions.some(ext => 
    url.toLowerCase().includes(ext)
  );

  if (!hasImageExtension) {
    logger.warn('VALIDATION', '画像URL拡張子警告', { url });
  }
};

/**
 * API キーの形式検証
 */
export const validateApiKey = (apiKey: string, serviceName: string): void => {
  const trimmed = validateAndTrim(apiKey);
  
  if (!trimmed) {
    throw ErrorHandler.handleValidationError(
      `${serviceName} APIキーが設定されていません。`,
      'apiKey'
    );
  }

  if (trimmed.length < 10) {
    throw ErrorHandler.handleValidationError(
      `${serviceName} APIキーの形式が正しくありません。`,
      'apiKey'
    );
  }
};

/**
 * 画像データの検証
 */
export interface ImageDataInput {
  url: string;
  width?: number;
  height?: number;
  content_type?: string;
}

export const validateImageData = (imageData: unknown): void => {
  const data = imageData as ImageDataInput;
  if (!data) {
    throw ErrorHandler.handleValidationError(
      '画像データが空です。',
      'imageData'
    );
  }

  if (!data.url) {
    throw ErrorHandler.handleValidationError(
      '画像URLが含まれていません。',
      'imageUrl'
    );
  }

  validateImageUrl(data.url);

  // width, height の検証
  if (data.width && (typeof data.width !== 'number' || data.width <= 0)) {
    logger.warn('VALIDATION', '画像幅データ警告', { width: data.width });
  }

  if (data.height && (typeof data.height !== 'number' || data.height <= 0)) {
    logger.warn('VALIDATION', '画像高さデータ警告', { height: data.height });
  }
};

/**
 * 汎用的な空値チェック
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  
  if (typeof value === 'string') {
    return validateAndTrim(value) === '';
  }
  
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  
  return false;
};

/**
 * 安全な文字列変換
 */
export const toSafeString = (value: any, defaultValue: string = ''): string => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  
  return String(value);
};

/**
 * バリデーション結果の型
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * 複数のバリデーションを実行し、結果をまとめる
 */
export const runValidations = (validations: (() => void)[]): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  for (const validation of validations) {
    try {
      validation();
    } catch (error) {
      if (error instanceof Error) {
        errors.push(error.message);
      } else {
        errors.push(String(error));
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};